/**
 * Encoder mínimo DICOM Part 10: Explicit VR Little Endian.
 * Solo los VR necesarios para GSPS/SR de sesión (sin pixel data).
 */

function concat(chunks) {
  let n = 0
  chunks.forEach((c) => {
    n += c.length
  })
  const out = new Uint8Array(n)
  let o = 0
  chunks.forEach((c) => {
    out.set(c, o)
    o += c.length
  })
  return out
}

function dvSet(bytes, fn, offset, value) {
  const b = bytes || new Uint8Array(4)
  const view = new DataView(b.buffer, b.byteOffset, b.byteLength)
  fn.call(view, offset, value, true)
  return b
}

function u16(n) {
  return dvSet(new Uint8Array(2), DataView.prototype.setUint16, 0, n)
}

function u32(n) {
  return dvSet(new Uint8Array(4), DataView.prototype.setUint32, 0, n)
}

function ascii(s) {
  const str = String(s || '')
  const b = new Uint8Array(str.length)
  for (let i = 0; i < str.length; i++) b[i] = str.charCodeAt(i) & 0xff
  return b
}

function padEven(b) {
  if (b.length % 2 === 0) return b
  const o = new Uint8Array(b.length + 1)
  o.set(b)
  o[b.length] = 0x20
  return o
}

const LONG_VR = { OB: 1, OW: 1, OF: 1, SQ: 1, UN: 1, UT: 1 }

function encodeElement(tag, vr, valueBytes) {
  const group = parseInt(tag.slice(1, 5), 16)
  const elem = parseInt(tag.slice(5, 9), 16)
  const val = valueBytes || new Uint8Array(0)
  const vrB = ascii(vr)
  if (LONG_VR[vr]) {
    return concat([u16(group), u16(elem), vrB, u16(0), u32(val.length), val])
  }
  return concat([u16(group), u16(elem), vrB, u16(val.length), val])
}

export function elCS(tag, s) {
  return encodeElement(tag, 'CS', padEven(ascii(s)))
}
export function elUI(tag, s) {
  const raw = ascii(s)
  const b = raw.length % 2 ? concat([raw, new Uint8Array([0])]) : raw
  return encodeElement(tag, 'UI', b)
}
export function elLO(tag, s) {
  return encodeElement(tag, 'LO', padEven(ascii(s)))
}
export function elPN(tag, s) {
  return encodeElement(tag, 'PN', padEven(ascii(s)))
}
export function elDA(tag, s) {
  return encodeElement(tag, 'DA', padEven(ascii(s)))
}
export function elTM(tag, s) {
  return encodeElement(tag, 'TM', padEven(ascii(s)))
}
export function elSH(tag, s) {
  return encodeElement(tag, 'SH', padEven(ascii(s)))
}
export function elST(tag, s) {
  return encodeElement(tag, 'ST', padEven(ascii(s)))
}
export function elIS(tag, s) {
  return encodeElement(tag, 'IS', padEven(ascii(s)))
}
export function elDS(tag, s) {
  return encodeElement(tag, 'DS', padEven(ascii(s)))
}
export function elUSMulti(tag, nums) {
  const arr = Array.isArray(nums) ? nums : [nums]
  const b = new Uint8Array(arr.length * 2)
  const view = new DataView(b.buffer)
  arr.forEach((v, i) => view.setUint16(i * 2, Number(v) || 0, true))
  return encodeElement(tag, 'US', b)
}
export function elUS(tag, n) {
  return elUSMulti(tag, [n])
}
export function elUL(tag, n) {
  return encodeElement(tag, 'UL', u32(n))
}

export function elFL(tag, floats) {
  const arr = Array.isArray(floats) ? floats : [floats]
  const b = new Uint8Array(arr.length * 4)
  const view = new DataView(b.buffer)
  arr.forEach((v, i) => view.setFloat32(i * 4, Number(v) || 0, true))
  return encodeElement(tag, 'FL', b)
}

export function elSQ(tag, items) {
  const parts = []
  ;(items || []).forEach((itemBytes) => {
    parts.push(u16(0xfffe), u16(0xe000), u32(itemBytes.length), itemBytes)
  })
  return encodeElement(tag, 'SQ', concat(parts))
}

export function newUid(prefix) {
  const base = prefix || '2.25'
  const n = `${Date.now()}${Math.floor(Math.random() * 1e9)}`
  return `${base}.${n}`
}

export function nowDicomDate() {
  const d = new Date()
  const p = (x) => String(x).padStart(2, '0')
  return `${d.getUTCFullYear()}${p(d.getUTCMonth() + 1)}${p(d.getUTCDate())}`
}

export function nowDicomTime() {
  const d = new Date()
  const p = (x) => String(x).padStart(2, '0')
  return `${p(d.getUTCHours())}${p(d.getUTCMinutes())}${p(d.getUTCSeconds())}`
}

function encodeFileMeta(metaBytes) {
  const groupLen = encodeElement('x00020000', 'UL', u32(metaBytes.length))
  return concat([groupLen, metaBytes])
}

export function elOB(tag, bytes) {
  const val = bytes || new Uint8Array(0)
  const padded = val.length % 2 ? concat([val, new Uint8Array([0])]) : val
  return encodeElement(tag, 'OB', padded)
}

/**
 * @param {Uint8Array} datasetBytes
 * @param {{ sopClassUid: string, sopInstanceUid: string }} ids
 */
export function wrapPart10(datasetBytes, ids) {
  const preamble = new Uint8Array(128)
  const dicm = ascii('DICM')
  const metaBody = concat([
    elOB('x00020001', new Uint8Array([0, 1])),
    elUI('x00020002', ids.sopClassUid),
    elUI('x00020003', ids.sopInstanceUid),
    elUI('x00020010', '1.2.840.10008.1.2.1'),
    elUI('x00020012', '2.25.16.0'),
    elSH('x00020013', '0.16.0'),
  ])
  const meta = encodeFileMeta(metaBody)
  return concat([preamble, dicm, meta, datasetBytes])
}

export { concat, encodeElement, ascii }
